"use client";

import posthog, { CaptureResult } from "posthog-js";
import { PostHogProvider } from "posthog-js/react";

import { FRONTEND_URL, POSTHOG_KEY } from "@/constants/constants";
import { POSTHOG_DEPLOYMENT } from "@/utils/posthog/constants";
import { isSerializedAbortException } from "@/utils/typeGuards/isSerializedAbortException";

import "@/lib/posthog/bundledSessionRecorder";

// Cancelled requests (tab close, navigation, input swap) get auto-captured as unhandled rejections; they are expected outcomes, so they are dropped before they leave the browser. Only the primary exception is tested (later entries are the cause chain, and a real error wrapping an abort must still ship) and only auto-captured events qualify (mechanism.handled === false), so deliberate captureException calls always pass through.
const dropAbortExceptions = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (event === null || event.event !== "$exception") {
    return event;
  }

  const exceptions =
    (event.properties.$exception_list as
      | {
          type?: string;
          value?: string;
          mechanism?: { handled?: boolean };
        }[]
      | undefined) ?? [];
  const primaryException = exceptions[0];
  if (primaryException === undefined) {
    return event;
  }

  const isAutoCapturedAbort =
    primaryException.mechanism?.handled === false &&
    isSerializedAbortException({
      type: primaryException.type,
      value: primaryException.value,
    });

  return isAutoCapturedAbort ? null : event;
};

// Stamp each event because consent changes and sign-out can clear PostHog's registered properties.
const stampDeploymentProperties = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (event !== null) {
    event.properties = {
      ...event.properties,
      ...POSTHOG_DEPLOYMENT.properties,
    };
  }
  return event;
};

if (typeof window !== "undefined") {
  posthog.init(POSTHOG_KEY, {
    api_host: `${FRONTEND_URL}/ingest`,
    ui_host: "https://us.posthog.com", // api_host points at our reverse proxy, so the PostHog app/toolbar links need the real host

    capture_pageview: false, // Disable automatic pageview capture, as we capture manually
    cookieless_mode:
      POSTHOG_DEPLOYMENT.isAutomaticCaptureEnabled ? undefined : "on_reject",
    loaded: () => {
      // Preview domains may never receive a Cookiebot response; clear a previously persisted opt-out as well as enabling capture for fresh sessions.
      if (
        POSTHOG_DEPLOYMENT.isAutomaticCaptureEnabled &&
        !posthog.has_opted_in_capturing()
      ) {
        // Automatic capture should not be counted as a user consent action.
        posthog.opt_in_capturing({ captureEventName: false });
      }
    },
    // An event dropped by the abort filter reaches the stamp step as null, which it passes through untouched.
    before_send: (event) =>
      stampDeploymentProperties(dropAbortExceptions(event)),
    session_recording: {
      captureCanvas: {
        canvasFps: 1,
        canvasQuality: "0.1",
      },
    },
  });
}

export function PHProvider({ children }: { children: React.ReactNode }) {
  return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
}
